import React from 'react';
import { Checkbox } from '@ohif/ui-next';
import {
  PEER_REVIEW_COLUMN_LABELS,
  type PeerReviewColumnKey,
} from '../../constants/peerReviewColumns';
import type { PeerReview } from '../../services/peerReviewService';
import { getPeerReviewStudyFields } from '../../services/peerReviewService';
import {
  displayOrDash,
  formatPeerReviewAge,
  formatPeerReviewModalityLabel,
  formatPeerReviewPriorityLabel,
  formatPeerReviewReportStatus,
  formatPeerReviewStudyDateTime,
  isPeerReviewUrgent,
  peerReviewModalityClass,
  peerReviewReportStatusClass,
} from './peerReviewUtils';

export type PeerReviewTableProps = {
  rows: PeerReview[];
  columnVisibility: Record<PeerReviewColumnKey, boolean>;
  selectedIds: Set<number>;
  onToggleRow: (id: number, checked: boolean) => void;
  onToggleAllPage: (checked: boolean) => void;
  onReview: (row: PeerReview) => void;
  openingReviewId?: number | null;
  /** Admin / technician: always View (cannot submit peer review). */
  viewOnly?: boolean;
};

export default function PeerReviewTable({
  rows,
  columnVisibility,
  selectedIds,
  onToggleRow,
  onToggleAllPage,
  onReview,
  openingReviewId = null,
  viewOnly = false,
}: Readonly<PeerReviewTableProps>) {
  const pageIds = rows.map(row => row.id);
  const allSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
  const someSelected = pageIds.some(id => selectedIds.has(id));
  const headerCheckState: boolean | 'indeterminate' = allSelected
    ? true
    : someSelected
      ? 'indeterminate'
      : false;

  const show = (key: PeerReviewColumnKey) => columnVisibility[key] !== false;
  const visibleCount = Object.values(columnVisibility).filter(v => v !== false).length;

  return (
    <div className="peer-review-table-outer">
      <table className="peer-review-table">
        <thead>
          <tr>
            <th
              className="peer-review-table__check-col"
              aria-label="Select all on this page"
            >
              <Checkbox
                checked={headerCheckState}
                onCheckedChange={checked => onToggleAllPage(checked === true)}
                className="h-4 w-4 border-slate-400 data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600"
              />
            </th>
            {show('patientName') && <th>{PEER_REVIEW_COLUMN_LABELS.patientName}</th>}
            {show('uhid') && <th>{PEER_REVIEW_COLUMN_LABELS.uhid}</th>}
            {show('age') && <th>{PEER_REVIEW_COLUMN_LABELS.age}</th>}
            {show('gender') && <th>{PEER_REVIEW_COLUMN_LABELS.gender}</th>}
            {show('studyDateTime') && <th>{PEER_REVIEW_COLUMN_LABELS.studyDateTime}</th>}
            {show('modality') && <th>{PEER_REVIEW_COLUMN_LABELS.modality}</th>}
            {show('studyDescription') && <th>{PEER_REVIEW_COLUMN_LABELS.studyDescription}</th>}
            {show('bodyPart') && <th>{PEER_REVIEW_COLUMN_LABELS.bodyPart}</th>}
            {show('referringPhysician') && <th>{PEER_REVIEW_COLUMN_LABELS.referringPhysician}</th>}
            {show('clinicalHistory') && <th>{PEER_REVIEW_COLUMN_LABELS.clinicalHistory}</th>}
            {show('priority') && <th>{PEER_REVIEW_COLUMN_LABELS.priority}</th>}
            {show('assignedRadiologist') && (
              <th>{PEER_REVIEW_COLUMN_LABELS.assignedRadiologist}</th>
            )}
            {show('reportStatus') && <th>{PEER_REVIEW_COLUMN_LABELS.reportStatus}</th>}
            <th className="peer-review-table__action-col">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={visibleCount + 2}
                className="peer-review-table__empty"
              >
                No peer reviews found.
              </td>
            </tr>
          ) : (
            rows.map(row => {
              const study = getPeerReviewStudyFields(row);
              const urgent = isPeerReviewUrgent(study.priority, study.is_critical);
              const statusLabel = formatPeerReviewReportStatus(row.status, study.report_status);
              const modalityLabel = formatPeerReviewModalityLabel(study.modality);

              return (
                <tr
                  key={row.id}
                  className={urgent ? 'peer-review-table__row--urgent' : undefined}
                >
                  <td className="peer-review-table__check-col">
                    <Checkbox
                      checked={selectedIds.has(row.id)}
                      onCheckedChange={checked => onToggleRow(row.id, checked === true)}
                      className="h-4 w-4 border-slate-400 data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600"
                    />
                  </td>
                  {show('patientName') && (
                    <td className="peer-review-table__patient">
                      {displayOrDash(study.patient_name)}
                    </td>
                  )}
                  {show('uhid') && <td>{displayOrDash(study.patient_uhid)}</td>}
                  {show('age') && <td>{formatPeerReviewAge(study.patient_age)}</td>}
                  {show('gender') && <td>{displayOrDash(study.patient_gender)}</td>}
                  {show('studyDateTime') && <td>{formatPeerReviewStudyDateTime(row)}</td>}
                  {show('modality') && (
                    <td>
                      {modalityLabel === '—' ? (
                        <span className="peer-review-empty">—</span>
                      ) : (
                        <span
                          className={`peer-review-modality-pill ${peerReviewModalityClass(study.modality)}`}
                        >
                          {modalityLabel}
                        </span>
                      )}
                    </td>
                  )}
                  {show('studyDescription') && <td>{displayOrDash(study.study_description)}</td>}
                  {show('bodyPart') && <td>{displayOrDash(study.body_part)}</td>}
                  {show('referringPhysician') && (
                    <td>{displayOrDash(study.referring_physician)}</td>
                  )}
                  {show('clinicalHistory') && (
                    <td className="peer-review-table__history">
                      {displayOrDash(study.clinical_history)}
                    </td>
                  )}
                  {show('priority') && (
                    <td>
                      <span
                        className={`peer-review-priority-pill ${
                          urgent
                            ? 'peer-review-priority-pill--urgent'
                            : 'peer-review-priority-pill--routine'
                        }`}
                      >
                        {formatPeerReviewPriorityLabel(study.priority)}
                      </span>
                    </td>
                  )}
                  {show('assignedRadiologist') && (
                    <td>{displayOrDash(study.assigned_radiologist_name)}</td>
                  )}
                  {show('reportStatus') && (
                    <td>
                      <span
                        className={`peer-review-status-pill ${peerReviewReportStatusClass(statusLabel)}`}
                      >
                        {statusLabel}
                      </span>
                    </td>
                  )}
                  <td className="peer-review-table__action-col">
                    <button
                      type="button"
                      className="peer-review-action-btn"
                      disabled={openingReviewId != null}
                      onClick={() => onReview(row)}
                    >
                      {openingReviewId === row.id
                        ? 'Opening…'
                        : viewOnly || String(row.status).toLowerCase() === 'submitted'
                          ? 'View'
                          : 'Review'}
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
